import { useQuery } from '@tanstack/react-query'
import {
  Chip,
  CircularProgress,
  Dialog, DialogContent, DialogTitle,
  IconButton,
  Stack,
  Table, TableBody, TableCell, TableHead, TableRow,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

import { governanceApi } from '../services/governance-api'

interface IProps {
  workspaceId: string
  open: boolean
  status?: string         // narrow to a STATUS bucket (RETAINED / SKIPED / READY / DELAYED)
  movementType?: string   // narrow to a movement type (inclusion / exclusion / change)
  title?: string
  onClose(): void
}

// RETAINED (criticada ou devolvida para correção) e SKIPED (retida) pedem ação.
// Até 01/10/2026 este mapa destacava RECTIFY e HELD, que ninguém grava.
export const STATUS_COLORS: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'> = {
  RETAINED: 'error',
  SKIPED: 'error',
  DELAYED: 'warning',
  READY: 'info',
  DONE: 'success',
}

/**
 * K4 (pending movements) drill-down. Pattern B — full PII.
 * Renders the operational pending-movements table for the selected workspace,
 * filtered by status / movement_type when provided.
 */
export function MovementsDrilldownDialog({ workspaceId, open, status, movementType, title, onClose }: IProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['governance', 'movements-drilldown', workspaceId, status, movementType],
    queryFn: () => governanceApi.movementsDrilldown({ workspaceId, status, movementType, limit: 200 }),
    enabled: open && !!workspaceId,
  })

  const rows = data?.data.rows ?? []

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6" component="div">{title ?? 'Movimentações pendentes'}</Typography>
          {status && <Chip size="small" label={status} color={STATUS_COLORS[status] ?? 'default'} />}
          {movementType && <Chip size="small" label={movementType} variant="outlined" />}
        </Stack>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && <CircularProgress size={20} />}
        {isError && <Typography color="error">Erro ao carregar movimentações pendentes.</Typography>}
        {data && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>CPF</TableCell>
                <TableCell>Nome</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Movimento</TableCell>
                <TableCell>Plano</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Dias pendente</TableCell>
                <TableCell>Motivo</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={`${r.person_tax_payer_id}-${i}`}>
                  <TableCell>{r.person_tax_payer_id}</TableCell>
                  <TableCell>{r.person_name ?? '—'}</TableCell>
                  <TableCell>{r.person_type ?? '—'}</TableCell>
                  <TableCell>{r.movement_type ?? '—'}</TableCell>
                  <TableCell>{r.plan ?? '—'}</TableCell>
                  <TableCell>
                    {r.status ? (
                      <Chip size="small" label={r.status} color={STATUS_COLORS[r.status] ?? 'default'} />
                    ) : '—'}
                  </TableCell>
                  <TableCell align="right">{r.days_pending}</TableCell>
                  <TableCell>{r.retained_reason ?? r.status_message ?? '—'}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center">Nenhuma movimentação pendente.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  )
}
